import {
  getLocalizedCountryName,
  isLocaleRegistered,
  registerLocale,
} from '@/utils/localizedCountryName';
import { useTranslations } from '@/utils/translations';
import { type CountryType, sortedCountries } from '@heidi/utils/countries';
import { useEffect, useMemo, useState } from 'react';

export interface LocalizedCountry extends CountryType {
  localizedName: string;
}

/** Country `labels` use short language keys (e.g. `zh`); UI locale may be BCP-47 (`zh-CN`). */
function pickCountryLabel(
  labels: CountryType['labels'] | undefined,
  locale: string
): string | undefined {
  if (!labels) return undefined;
  if (labels[locale] !== undefined) return labels[locale];
  try {
    const lang = new Intl.Locale(locale).language;
    if (labels[lang] !== undefined) return labels[lang];
  } catch {
    // invalid locale string — fall through to Intl / i18n library name
  }
  return undefined;
}

export function useLocalizedCountries() {
  const { locale, i18n } = useTranslations();
  // Localize off the active i18next language (tracks scoped onboarding instance), not router `locale`.
  const activeLanguage = i18n?.language ?? locale;
  const [isLocaleReady, setIsLocaleReady] = useState(isLocaleRegistered(activeLanguage));

  useEffect(() => {
    let mounted = true;
    if (!isLocaleRegistered(activeLanguage)) {
      setIsLocaleReady(false);
      void registerLocale(activeLanguage).then(() => mounted && setIsLocaleReady(true));
    } else {
      setIsLocaleReady(true);
    }
    return () => {
      mounted = false;
    };
  }, [activeLanguage]);

  const localizedCountries: LocalizedCountry[] = useMemo(
    () =>
      sortedCountries.map((c) => ({
        ...c,
        localizedName:
          pickCountryLabel(c.labels, activeLanguage) ??
          (c.countryCode
            ? getLocalizedCountryName(c.countryCode, activeLanguage)
            : c.name),
      })),
    [activeLanguage, isLocaleReady] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const getCountryLocalizedName = (country: CountryType) =>
    pickCountryLabel(country.labels, activeLanguage) ??
    (country.countryCode
      ? getLocalizedCountryName(country.countryCode, activeLanguage)
      : country.name);

  const getLocalizedNameFromEnglish = (englishName: string) => {
    const country = sortedCountries.find((c) => c.name === englishName);
    if (!country) return englishName;
    return (
      pickCountryLabel(country.labels, activeLanguage) ??
      (country.countryCode
        ? getLocalizedCountryName(country.countryCode, activeLanguage)
        : englishName)
    );
  };

  return {
    localizedCountries,
    locale,
    isLocaleReady,
    getCountryLocalizedName,
    getLocalizedNameFromEnglish,
  };
}
